import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CreateTokenForm } from '../components/explore/CreateTokenSection';
import { computeLaunchParams, formatTokenAmount, LaunchParams } from '../utils/mavrkMath';
import { getEthUsdPrice } from '../utils/ethPrice';
import { PageShell } from '../components/shared/PageShell';

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: var(--mv-primary, #a855f7);
  margin-bottom: 8px;
  
  @media (max-width: 700px) {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #9ca3af;
  font-family: 'Inter', sans-serif;
`;

const PricingPanel = styled.div`
  background: var(--mv-bg, #000000);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(10px);
  margin-bottom: 24px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: var(--mv-font, #ffffff);
  margin-bottom: 16px;
  font-family: 'Inter', sans-serif;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--mv-font, #ffffff);
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  background: var(--mv-input-bg, #000000);
  border: 1px solid color-mix(in srgb, var(--mv-primary, #a855f7) 30%, transparent);
  border-radius: 8px;
  padding: 12px 14px;
  color: var(--mv-font, #ffffff);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--mv-primary, #a855f7);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }
`;

const HelperText = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 6px;
  font-family: 'Inter', sans-serif;
`;

const MarketCapDisplay = styled.div`
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
`;

const MarketCapLabel = styled.div`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const MarketCapValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--mv-primary, #a855f7);
  font-family: 'Inter', sans-serif;
`;

const DebugGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ScenarioPanel = styled.div`
  background: var(--mv-bg, #000000);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(10px);
`;

const ScenarioTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--mv-primary, #a855f7);
  margin-bottom: 16px;
  font-family: 'Inter', sans-serif;
`;

const DebugSection = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const DebugLabel = styled.div`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const DebugValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #00ff00;
  background: rgba(0, 255, 0, 0.05);
  padding: 8px 12px;
  border-radius: 6px;
  word-break: break-all;
  border: 1px solid rgba(0, 255, 0, 0.2);
`;

const ErrorMessage = styled.div`
  background: rgba(255, 89, 60, 0.1);
  border: 1px solid #FF593C;
  color: #FF593C;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 12px;
`;

const Select = styled.select`
  width: 100%;
  background: var(--mv-input-bg, #000000);
  border: 1px solid color-mix(in srgb, var(--mv-primary, #a855f7) 30%, transparent);
  border-radius: 8px;
  padding: 12px 14px;
  color: var(--mv-font, #ffffff);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--mv-primary, #a855f7);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary, #a855f7) 10%, transparent);
  }
  
  option {
    background: var(--mv-bg, #000000);
    color: var(--mv-font, #ffffff);
  }
`;

type QuoteCurrency = 'ETH' | 'WMON';

const RndTokenPage: React.FC = () => {
  const navigate = useNavigate();
  const [startingPriceUSD, setStartingPriceUSD] = useState('0.0000025'); // USD per token
  const [quoteCurrency, setQuoteCurrency] = useState<QuoteCurrency>('ETH');
  const [ethUsdPrice, setEthUsdPrice] = useState(2500);
  const [wmonUsdPrice, setWmonUsdPrice] = useState(0.92);
  const [launchParams, setLaunchParams] = useState<LaunchParams | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch ETH price on mount
  useEffect(() => {
    const fetchEthPrice = async () => {
      try {
        const price = await getEthUsdPrice();
        setEthUsdPrice(price);
      } catch (err) {
        console.error('Failed to fetch ETH price:', err);
        // Keep default 2500
      }
    };
    fetchEthPrice();
  }, []);

  // Get current quote currency price
  const getQuoteCurrencyPrice = (): number => {
    return quoteCurrency === 'ETH' ? ethUsdPrice : wmonUsdPrice;
  };

  // Get quote currency symbol
  const getQuoteCurrencySymbol = (): string => {
    return quoteCurrency === 'ETH' ? 'ETH/WETH' : 'WMON';
  };

  // Compute launch params whenever price changes
  useEffect(() => {
    try {
      const priceUSD = parseFloat(startingPriceUSD);
      if (isNaN(priceUSD) || priceUSD <= 0) {
        setError('Please enter a valid price greater than 0');
        setLaunchParams(null);
        return;
      }

      const quotePrice = quoteCurrency === 'ETH' ? ethUsdPrice : wmonUsdPrice;
      if (quotePrice <= 0) {
        setError(`Please enter a valid ${quoteCurrency === 'ETH' ? 'ETH/WETH' : 'WMON'} price`);
        setLaunchParams(null);
        return;
      }

      // Convert USD price to quote currency price
      // USD per token / USD per quote currency = quote currency per token
      const priceInQuoteCurrency = priceUSD / quotePrice;

      const params = computeLaunchParams(priceInQuoteCurrency, quotePrice);
      setLaunchParams(params);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to compute launch parameters');
      setLaunchParams(null);
    }
  }, [startingPriceUSD, ethUsdPrice, wmonUsdPrice, quoteCurrency]);

  return (
    <PageShell>
      <PageHeader>
        <PageTitle>🔬 R&D: Configurable Token Pricing</PageTitle>
        <PageSubtitle>
          Experimental page for testing user-configurable starting prices (BOTH token orderings)
        </PageSubtitle>
      </PageHeader>

      {/* Pricing Configuration */}
      <PricingPanel>
        <PanelTitle>💰 Pricing Configuration</PanelTitle>

        <InputGroup>
          <Label>Quote Currency</Label>
          <Select
            value={quoteCurrency}
            onChange={(e) => setQuoteCurrency(e.target.value as QuoteCurrency)}
          >
            <option value="ETH">ETH/WETH</option>
            <option value="WMON">WMON (Wrapped Monad)</option>
          </Select>
          <HelperText>
            Select the quote currency for pool calculations
          </HelperText>
        </InputGroup>

        {quoteCurrency === 'WMON' && (
          <InputGroup>
            <Label>WMON Price (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={wmonUsdPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value) && value > 0) {
                  setWmonUsdPrice(value);
                }
              }}
              placeholder="0.92"
            />
            <HelperText>
              Current WMON price in USD (default: $0.92)
            </HelperText>
          </InputGroup>
        )}

        <InputGroup>
          <Label>Starting Price per Token (in USD)</Label>
          <Input
            type="text"
            value={startingPriceUSD}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartingPriceUSD(e.target.value)}
            placeholder="0.0000025"
          />
          <HelperText>
            {getQuoteCurrencySymbol()} Price: ${getQuoteCurrencyPrice().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Equivalent: {(parseFloat(startingPriceUSD || '0') / getQuoteCurrencyPrice()).toExponential(4)} {quoteCurrency}
          </HelperText>
        </InputGroup>

        {launchParams && (
          <MarketCapDisplay>
            <MarketCapLabel>Starting Market Cap</MarketCapLabel>
            <MarketCapValue>
              ${launchParams.marketCapUSD.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
            </MarketCapValue>
            <HelperText style={{ marginTop: '8px' }}>
              Price: ${startingPriceUSD} USD per token ({launchParams.pricePerTokenETH} {quoteCurrency})
            </HelperText>
          </MarketCapDisplay>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </PricingPanel>

      {/* Debug Panel - BOTH Scenarios */}
      {launchParams && (
        <>
          <PanelTitle style={{ marginBottom: '16px' }}>
            🔍 Computed Parameters (Both Token Orderings)
          </PanelTitle>

          <DebugGrid>
            {/* Scenario 1: Token0 */}
            <ScenarioPanel>
              <ScenarioTitle>📊 Scenario 1: New Token is token0</ScenarioTitle>
              <HelperText style={{ marginBottom: '16px' }}>
                When new token address &lt; {getQuoteCurrencySymbol()} address
              </HelperText>

              <DebugSection>
                <DebugLabel>sqrtPriceX96</DebugLabel>
                <DebugValue>{launchParams.paramsToken0.sqrtPriceX96}</DebugValue>
                <HelperText style={{ marginTop: '6px' }}>
                  Hex: 0x{BigInt(launchParams.paramsToken0.sqrtPriceX96).toString(16)}
                </HelperText>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Tick Range</DebugLabel>
                <DebugValue>
                  Lower: {launchParams.paramsToken0.tickLower}<br />
                  Upper: {launchParams.paramsToken0.tickUpper}<br />
                  Range: {launchParams.paramsToken0.tickUpper - launchParams.paramsToken0.tickLower} ticks
                </DebugValue>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Amount0 (New Token)</DebugLabel>
                <DebugValue>
                  Desired: {formatTokenAmount(launchParams.paramsToken0.amount0Desired)}<br />
                  Minimum: {formatTokenAmount(launchParams.paramsToken0.amount0Min)}
                </DebugValue>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Amount1 ({getQuoteCurrencySymbol()})</DebugLabel>
                <DebugValue>
                  Desired: {formatTokenAmount(launchParams.paramsToken0.amount1Desired)}<br />
                  Minimum: {formatTokenAmount(launchParams.paramsToken0.amount1Min)}
                </DebugValue>
              </DebugSection>
            </ScenarioPanel>

            {/* Scenario 2: Token1 */}
            <ScenarioPanel>
              <ScenarioTitle>📊 Scenario 2: New Token is token1</ScenarioTitle>
              <HelperText style={{ marginBottom: '16px' }}>
                When {getQuoteCurrencySymbol()} address &lt; new token address
              </HelperText>

              <DebugSection>
                <DebugLabel>sqrtPriceX96</DebugLabel>
                <DebugValue>{launchParams.paramsToken1.sqrtPriceX96}</DebugValue>
                <HelperText style={{ marginTop: '6px' }}>
                  Hex: 0x{BigInt(launchParams.paramsToken1.sqrtPriceX96).toString(16)}
                </HelperText>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Tick Range</DebugLabel>
                <DebugValue>
                  Lower: {launchParams.paramsToken1.tickLower}<br />
                  Upper: {launchParams.paramsToken1.tickUpper}<br />
                  Range: {launchParams.paramsToken1.tickUpper - launchParams.paramsToken1.tickLower} ticks
                </DebugValue>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Amount0 ({getQuoteCurrencySymbol()})</DebugLabel>
                <DebugValue>
                  Desired: {formatTokenAmount(launchParams.paramsToken1.amount0Desired)}<br />
                  Minimum: {formatTokenAmount(launchParams.paramsToken1.amount0Min)}
                </DebugValue>
              </DebugSection>

              <DebugSection>
                <DebugLabel>Amount1 (New Token)</DebugLabel>
                <DebugValue>
                  Desired: {formatTokenAmount(launchParams.paramsToken1.amount1Desired)}<br />
                  Minimum: {formatTokenAmount(launchParams.paramsToken1.amount1Min)}
                </DebugValue>
              </DebugSection>
            </ScenarioPanel>
          </DebugGrid>
        </>
      )}

      {/* Original Create Token Form (for reference) */}
      <div style={{ marginTop: '40px' }}>
        <PanelTitle style={{ marginBottom: '16px' }}>
          📝 Token Creation Form (Standard Flow)
        </PanelTitle>
        <CreateTokenForm
          isVisible={true}
          asPage={true}
          onClose={() => navigate('/explore')}
        />
      </div>
    </PageShell>
  );
};

export default RndTokenPage;
